import { Autocomplete, Button, FormControl, Grid, IconButton, TextField } from "@mui/material";
import { lazy, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";

import { userContext } from "src/context/userContext";
import { KeikkaDataContext } from "src/context/KeikkaDataContext";

import ProgressCirc from "../../layout/Progress/ProgressCirc";
import IconsContainer from "src/components/icons/others/IconsContainer";
import LazyLoader from "src/components/layout/Progress/LazyLoader";
import { AddPersonIcon } from "src/components/icons/PersonIcons";
import { DeleteIcon } from "src/components/icons/ActionIcons";

const EditPersonModal = lazy(() => import("src/components/person/editPerson/EditPersonModal"));

export default function PersonSelectorDropDown(props) {
  const { token, user } = useContext(userContext) || {};
  const { keikkaData } = useContext(KeikkaDataContext) || {};

  const asiakasId = props.asiakasId;
  const tyomaaId = props.tyomaaId;
  const keikkaId = props.keikkaId;
  const personId = user.personId;
  const variant = props.variant || "outlined";
  const showEmail = props.showEmail === false ? false : true;
  const showPhone = props.showPhone === false ? false : true;

  const [selectedPersonId, setSelectedPersonId] = useState(props.defaultPersonId);
  const [personList, setPersonList] = useState(null);
  const [editPersonIsOpen, setEditPersonIsOpen] = useState(false);
  const [createNewPersonState, setCreateNewPersonState] = useState(false);
  const [autoCompleteKey, setAutoCompleteKey] = useState(1);
  const [useEffectKey, setUseEffectKey] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [inputValue, setInputValue] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const asiakasPersonSettingTypeId = props.asiakasPersonSettingTypeId || 0; // 0=all, 8 = isPumppari

  useEffect(() => {
    let isMounted = true;
    const getData = async () => {
      const personId = user.personId;
      let addr = "";
      if (asiakasId)
        addr = "/api/asiakas/person/list/" + asiakasId + "/" + asiakasPersonSettingTypeId;
      else if (tyomaaId) addr = "/api/tyomaa/person/list/" + tyomaaId + "/" + personId;
      else if (keikkaId) addr = "/api/keikka/person/list/" + keikkaId + "/" + personId;
      else console.log("NO PERSONS TO FETCH");
      return fetch(import.meta.env.VITE_SERVER_URL + addr, {
        headers: {
          "x-access-token": `${token}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          const personList = data.personList || data;
          const asiakasPersonSettings = data.asiakasPersonSettings || [];
          const personListWithSettings = personList.map((person) => {
            const _asiakasPersonSetting = asiakasPersonSettings.find(
              (ps) => ps.asiakasPersonId === person.asiakasPersonId
            );
            if (_asiakasPersonSetting) {
              person.asiakasPersonSetting = _asiakasPersonSetting;
            }
            return person;
          });
          if (isMounted) setPersonList(personListWithSettings);
          setSelectedPersonId(props.defaultPersonId);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    };
    getData();
    return () => (isMounted = false);
  }, [useEffectKey, props.asiakasId, props.keikkaId, props.tyomaaId, props.defaultPersonId]);

  const getPerson = (personId) => {
    let selectedPerson = null;
    if (personList == null) return "";
    personList.map((person) => {
      if (person.personId === personId) selectedPerson = person;
    });
    return selectedPerson;
  };
  const editPersonClick = () => setEditPersonIsOpen(true);
  const onInputChange = (inputValue) => setInputValue(inputValue);

  const onChange = (inputValue) => {
    if (!inputValue) {
      setSelectedPersonId(0);
      props.setPerson(0);
    } else if (inputValue.personId > 0) {
      setSelectedPersonId(inputValue.personId);
      props.setPerson(getPerson(inputValue.personId));
    } else {
      resetAutoComplete();
      return false;
    }
  };
  const resetAutoComplete = () => {
    setAutoCompleteKey(autoCompleteKey + 1);
  };
  const sortedPersonList = () => {
    if (personList == null) return [];
    return personList.sort((a, b) => {
      var email1 = a.personEmail ? a.personEmail.toUpperCase() : ""; // ignore upper and lowercase
      var email2 = b.personEmail ? b.personEmail.toUpperCase() : ""; // ignore upper and lowercase
      if (email1 < email2) {
        return -1;
      }
      if (email1 > email2) {
        return 1;
      }
      // names must be equal
      return 0;
    });
  };
  const handleEditPersonChange = (personId) => {
    setUseEffectKey(useEffectKey + 1);
    resetAutoComplete();
    props.onChangePerson(personId);
    setEditPersonIsOpen(false);
  };
  const handleEditPersonClose = () => setEditPersonIsOpen(false);

  const handlePersonAddIconClick = async () => {
    setEditPersonIsOpen(true);
    setCreateNewPersonState(true);
  };

  return (
    <>
      <LazyLoader>
        <EditPersonModal
          personId={createNewPersonState ? 0 : selectedPersonId}
          isOpen={editPersonIsOpen}
          onClose={handleEditPersonClose}
          onChange={handleEditPersonChange}
        />
      </LazyLoader>
      {personList && (
        <div className="spaceBetween">
          <div style={{ width: "100%" }}>
            {isLoading ? (
              <ProgressCirc isLoading={isLoading} />
            ) : (
              <>
                <FormControl fullWidth>
                  <Autocomplete
                    disabled={props.disabled}
                    autoHighlight
                    selectOnFocus
                    key={autoCompleteKey}
                    id="asiakasmain-autocomplete"
                    options={sortedPersonList()}
                    value={getPerson(selectedPersonId)}
                    sx={{ width: "98%" }}
                    getOptionLabel={(option) => {
                      const p =
                        option.personPhone && showPhone ? " (" + option.personPhone + ")" : "";
                      return (
                        (option.personFirstName ? option.personFirstName : "") +
                        " " +
                        (option.personLastName ? option.personLastName : "") +
                        p
                      );
                    }}
                    onChange={(event, newValue) => {
                      onChange(newValue);
                    }}
                    onInputChange={(event, newValue) => {
                      onInputChange(newValue);
                    }}
                    renderOption={(props, option) => (
                      <Grid
                        container
                        {...props}
                        sx={{ paddingLeft: "15px", paddingRight: "15px", minWidth: "200px" }}
                        key={option.personId}>
                        <Grid size={{ xs: 8 }}>
                          {option.personFirstName} {option.personLastName}
                          {showEmail && (
                            <>
                              <br />
                              {option.personEmail}
                            </>
                          )}
                        </Grid>
                        {showPhone && <Grid size={{ xs: 4 }}>{option.personPhone}</Grid>}
                      </Grid>
                    )}
                    renderInput={(params) => {
                      return (
                        <TextField
                          {...params}
                          label={props.label || "Henkilö"}
                          variant={variant}
                          fullWidth
                          inputProps={{
                            ...params.inputProps,
                          }}
                        />
                      );
                    }}
                    filterOptions={(options, state) => {
                      return options.filter((option) =>
                        (
                          option.personFirstName +
                          " " +
                          option.personLastName +
                          option.personPhone +
                          option.personEmail
                        )
                          .toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                      );
                    }}
                    // filterOptions={(options, params) => filter(options, params)}
                  />
                </FormControl>
              </>
            )}
          </div>
          {!isLoading && props.allowNull && selectedPersonId > 0 && (
            <IconButton
              onClick={() => {
                setSelectedPersonId(null);
                props.setPerson(null);
              }}>
              <DeleteIcon />
            </IconButton>
          )}
          {!isLoading && (props.showEditIcon || selectedPersonId == personId) && (
            <Button
              sx={{ height: "55px" }}
              variant="outlined"
              onClick={editPersonClick}
              aria-label="Muokkaa henkilöä">
              <IconsContainer
                showEdit
                showWarning={
                  keikkaData && keikkaData.keikkaContactPerson.personError().errorTxt !== null
                }
              />
            </Button>
          )}
          {!isLoading && props.showAddPersonIcon && (
            <Button
              variant="outlined"
              onClick={() => handlePersonAddIconClick()}
              aria-label="Lisää henkilö">
              <AddPersonIcon />
            </Button>
          )}
        </div>
      )}
    </>
  );
}

PersonSelectorDropDown.propTypes = {
  asiakasId: PropTypes.number,
  tyomaaId: PropTypes.number,
  keikkaId: PropTypes.number,
  defaultPersonId: PropTypes.number,
  showEditIcon: PropTypes.bool,
  showAddPersonIcon: PropTypes.bool,
  disabled: PropTypes.bool,
  onAddNewPerson: PropTypes.func,
  setPerson: PropTypes.func,
  onChangePerson: PropTypes.func,
  asiakasPersonSettingTypeId: PropTypes.number,
  label: PropTypes.string,
  variant: PropTypes.string,
  showEmail: PropTypes.bool,
  showPhone: PropTypes.bool,
  allowNull: PropTypes.bool,
};
